import { lazy } from "react";
import { Navigate } from "react-router-dom";
import AuthGuard from "./auth/AuthGuard";
import { authRoles } from "./auth/authRoles";
import Loadable from "./components/Loadable";
import LogInLayout from "./utils/LogInLayout";
import EmployeeGuard from "./auth/EmployeeGuard";
import CheckInOutGaurd from "./auth/CheckInOutGaurd";
import CompanyGuard from "./auth/CompanyGuard";
// Layout shell only — stays eager, it's the chrome every marketing page
// renders into. The page content rendered inside it is lazy-loaded below.
import RedesignShell from "./views/sessions/RedesignShell";
// NOTE: HomePage, Blog1, and Pricing (the non-redesign versions) were
// imported here but never referenced by any route — removed rather than
// converted, since an unused eager import is pure dead weight either way.
// import materialRoutes from 'app/views/material-kit/MaterialRoutes';

// admin shell — sidebar/topbar/customizer/notifications. Used only by the
// four authenticated route groups below, but was a static import, so it
// was riding into the marketing bundle on every visit regardless of route.
const MatxLayout = Loadable(
  lazy(() => import("app/components/MatxLayout/MatxLayout")),
);

// EmployeeSystemTab was the actual source of the echarts (~545-module) leak
// into the eager bundle: it statically imported LeaveReport → LeaveTypeChart
// → echarts-for-react. These four were the only admin views still using a
// plain static import instead of the Loadable(lazy(...)) pattern every
// other admin route already uses.
const EmployeeSystemTab = Loadable(
  lazy(() => import("app/views/employees/EmployeeSystemTab")),
);
const EmployeeDetails = Loadable(
  lazy(() => import("app/views/employees/EmployeeDetails")),
);
const EmployeeDetailsTab = Loadable(
  lazy(() => import("app/views/employees/EmployeeDetailsTab")),
);
const EmployeProfileTab = Loadable(
  lazy(() => import("app/views/employees/EmployeProfileTab")),
);
// Last remaining static admin import — pulled in the full @mui/x-date-pickers
// library (81 modules) eagerly for a single leave-type config form.
const AddLeaveType = Loadable(
  lazy(() => import("app/views/leaveManagement/admin/AddEditLeaveType")),
);

// session pages
const NotFound = Loadable(lazy(() => import("app/views/sessions/NotFound")));
const JwtLogin = Loadable(lazy(() => import("app/views/sessions/JwtLogin")));
const JwtAdminLogin = Loadable(
  lazy(() => import("app/views/sessions/JwtAdminLogin")),
);
const JwtLRegisterCompany = Loadable(
  lazy(() => import("app/views/sessions/JwtLRegisterCompany")),
);
const VerifyEmail = Loadable(
  lazy(() => import("app/views/sessions/VerifyEmail")),
);
const JwtRegister = Loadable(
  lazy(() => import("app/views/sessions/JwtRegister")),
);
const ForgotPassword = Loadable(
  lazy(() => import("app/views/sessions/ForgotPassword")),
);
const AdminForgotPassword = Loadable(
  lazy(() => import("app/views/sessions/AdminForgotPassword")),
);

// marketing / public landing + blog pages — split into their own chunks so
// a marketing visitor never downloads the authenticated admin app, and an
// admin session never downloads marketing/blog content it doesn't render.
const OfferLanding = Loadable(
  lazy(() => import("app/views/sessions/offer/OfferLanding")),
);
const HomePageRedesign = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/HomePageRedesign")),
);
const PricingRedesign = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/PricingRedesign")),
);
const AttendanceSolution = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/solutions/AttendanceSolution")),
);
const LeaveSolution = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/solutions/LeaveSolution")),
);
const ActivityTrackingSolution = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/solutions/ActivityTrackingSolution")),
);
const TimesheetSolution = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/solutions/TimesheetSolution")),
);
const VisitorSolution = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/solutions/VisitorSolution")),
);
const ActivityTracking = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/ActivityTracking")),
);
const LeaveMangement = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/LeaveMangement")),
);
const RealTimeInsights = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/RealTimeInsight")),
);
const WorkFromHome = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/WorkFromHome")),
);
const Blogs = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/Blogs")),
);
const FAQ = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/Faq")),
);
const DetailedComplianceReports = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/DetailedComplianceReports")),
);
const VisitorMangement = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/VisitorManegement")),
);
const ContactUs = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/ContactUs")),
);
const AboutUs = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/AboutUs")),
);

// blog posts — one chunk each, fetched only when that specific post is opened
const AttandanceManagement = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/AutomatedAttendanceManagement")),
);
const HowAttendanceManagementSoftwareImproves = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/HowAttendanceManagementSoftwareImproves")),
);
const TopBenefitsofImplementingAttendance = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/TopBenefitsofImplementingAttendance")),
);
const HowBoostsProductivityandReducesCosts = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/HowBoostsProductivityandReducesCosts")),
);
const TheFutureofEmployeeTime = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/TheFutureofEmployeeTime")),
);
const WhyYourComapnyNeeds = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/WhyYourComapnyNeeds")),
);
const AdvancedTrackingSolutionsHelp = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/AdvancedTrackingSolutionsHelp")),
);
const StreamliningHROperations = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/StreamliningHROperations")),
);
const KeyFeaturestoLookAttandance = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/KeyFeaturestoLookAttandance")),
);
const CommonAttendanceManagement = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/5CommonAttendanceManagement")),
);
const BoostingEmployeeAccountability = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/BoostingEmployeeAccountability")),
);
const Enhancingremotework = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/Enhancingremotework")),
);
const TheImportanceofAttendance = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/TheImportanceofAttendance")),
);
const HowtochoosetherightAttendancemanagement = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/HowtochoosetherightAttendancemanagement")),
);
const RealTimeAttendanceTrackingBenefits = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/Real-TimeAttendanceTrackingBenefits")),
);
const AutomatedAttendanceSolutions = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/AutomatedAttendanceSolutions")),
);
const BoostBusinessEfficiency = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/BoostBusinessEfficiency")),
);
const OptimizingEmployeeSchedulesWithAttendanceManagementSoftware = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/OptimizingEmployeeSchedulesWithAttendanceManagementSoftware")),
);
const HRTechnologyTrendsToWatchOut = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/HRTechnologyTrendsToWatchOut")),
);
const TheBestWayToTrackTimeAttendanceOfYourRemoteTeam = Loadable(
  lazy(() => import("app/views/sessions/Landing-page-component/blog-component/TheBestWayToTrackTimeAttendanceOfYourRemoteTeam")),
);

// echart page
const AppEchart = Loadable(
  lazy(() => import("app/views/charts/echarts/AppEchart")),
);
// comapony  page
const CompanyList = Loadable(lazy(() => import("app/views/company/list")));
const AddCompany = Loadable(lazy(() => import("app/views/company/add")));
// employees page
const EmployeesTable = Loadable(lazy(() => import("app/views/employees/list")));
const AddEmployee = Loadable(lazy(() => import("app/views/employees/add")));
const EditEmployee = Loadable(lazy(() => import("app/views/employees/edit")));
const AdminList = Loadable(lazy(() => import("app/views/employees/AdminList")));
// const TodayAttendaceList = Loadable(lazy(() => import("app/views/employees/attendance/todayList")));

// checkinout
const Checkinout = Loadable(
  lazy(() => import("app/views/checkinout/checkinout")),
);
const Checkoutlist = Loadable(
  lazy(() => import("app/views/checkinout/checkoutlist")),
);
const EmployeeAttendanceList = Loadable(
  lazy(() => import("app/views/checkinout/attendanceList")),
);
const EmployeeLeave = Loadable(
  lazy(() => import("app/views/checkinout/EmployeeLeave")),
);

// Roles
const RoleList = Loadable(lazy(() => import("app/views/roles/roles")));
const AddRole = Loadable(lazy(() => import("app/views/roles/AddRoles")));
const EditRole = Loadable(lazy(() => import("app/views/roles/EditRole")));
// const Checkoutlist = Loadable(lazy(() => import('app/views/checkinout/checkoutlist')));
const Approval = Loadable(lazy(() => import("app/views/Deskstop/ApproveList")));
// stting page
const Setting = Loadable(lazy(() => import("app/views/setting/Setting")));
const SystemSettings = Loadable(lazy(() => import("app/views/setting/SystemSettings")));
//profile
const Profile = Loadable(lazy(() => import("app/views/profile/profile")));
const ChangePassword = Loadable(
  lazy(() => import("app/views/profile/ChangePassword")),
);

// dashboard page
const Analytics = Loadable(lazy(() => import("app/views/dashboard/Analytics")));
// Report Page
const ReportsTab = Loadable(lazy(() => import("app/views/reports/ReportsTab")));
const ReportsHub = Loadable(lazy(() => import("app/views/reports/ReportsHub")));
// Leave Page
const Leaves = Loadable(
  lazy(() => import("app/views/employees/leaves/LeaveTab")),
);
const LandingPage = Loadable(
  lazy(() => import("app/views/sessions/LandingPage")),
);

//vistor
const VisitorList = Loadable(lazy(() => import("app/views/visitor/list")));

//Holiday
const Holiday = Loadable(lazy(() => import("app/views/holiday/Holiday")));
const AddHoliday = Loadable(
  lazy(() => import("app/views/holiday/Add_Holiday")),
);
const HolidayCalender = Loadable(
  lazy(() => import("app/views/holiday/HolidayTab")),
);

//Employee Dashboard
const EmployeeDashboard = Loadable(
  lazy(() => import("app/views/employee-dashboard/employee-dashboard")),
);
//Employee calende
const EmployeeCalendar = Loadable(
  lazy(() => import("app/views/employee-calendar/employee-calendar")),
);

//Employee logs
const EmployeActivityTab = Loadable(
  lazy(() => import("app/views/employee-log/ActivityTracking")),
);

// App Management
const AppManagement = Loadable(
  lazy(() => import("app/views/employee-log/AppManagement")),
);

//System Dashbaord
const SystemDashboard = Loadable(
  lazy(() => import("app/views/system-dashboard/systemDashboard")),
);

//System Dashbaord
const Requests = Loadable(
  lazy(() => import("app/views/employees/requests/requests")),
);
// project label
const ProjectLabel = Loadable(
  lazy(() => import("app/views/project-label/project-label")),
);
//Leave Management system

const LeaveType = Loadable(
  lazy(() => import("app/views/leaveManagement/admin/LeaveType")),
);
const LeaveTracker = Loadable(
  lazy(() => import("app/views/leaveManagement/admin/LeaveTracker")),
);
const EmployeeLeaveList = Loadable(
  lazy(() => import("app/views/leaveManagement/admin/EmployeeLeaves")),
);
const LeaveCofiguration = Loadable(
  lazy(() => import("app/views/leaveManagement/admin/LeaveConfiguration")),
);
const LeaveSetup = Loadable(
  lazy(() => import("app/views/leaveManagement/admin/LeaveSetup")),
);
const LeaveBalances = Loadable(
  lazy(() => import("app/views/leaveManagement/admin/LeaveBalances")),
);

const LeaveReport = Loadable(
  lazy(() => import("app/views/leaveManagement/admin/LeaveReport")),
);

const EmployeeLeaveBalance = Loadable(
  lazy(() => import("app/views/leaveManagement/admin/EmployeeLeaveBalance")),
);
const LeaveReconcilation = Loadable(
  lazy(() => import("app/views/leave-reconcilation/LeaveReconcilation")),
);

const CronJobs = Loadable(lazy(() => import("app/views/cron-job/cron-job")));

const Plan = Loadable(lazy(() => import("app/views/company-plan/Plan")));

const EmployeeActivityLogsData = Loadable(
  lazy(
    () =>
      import("app/views/employee-log/employee-activity/EmployeeActivityLogData"),
  ),
);

const EmployeLopTab = Loadable(
  lazy(
    () =>
      import("app/views/leaveManagement/admin/lop-adjustment/EmployeLopTab"),
  ),
);

// Timesheet Management
const ProjectManagement = Loadable(
  lazy(() => import("app/views/projects/ProjectManagement")),
);
const TimesheetApproval = Loadable(
  lazy(() => import("app/views/timesheet-approval/TimesheetApproval")),
);
const TimesheetReport = Loadable(
  lazy(() => import("app/views/timesheet-report/TimesheetReport")),
);
const TimesheetEntry = Loadable(
  lazy(() => import("app/views/timesheet-entry/TimesheetEntry")),
);
const ClientApprovalView = Loadable(
  lazy(() => import("app/views/client-approval/ClientApprovalView")),
);

const routes = [
  {
    element: (
      <AuthGuard>
        <MatxLayout />
      </AuthGuard>
    ),
    children: [
      // ...materialRoutes,
      // dashboard route
      {
        path: "/dashboard",
        element: <Analytics />,
        auth: authRoles.admin,
      },

      // e-chart rooute
      {
        path: "/charts/echarts",
        element: <AppEchart />,
        auth: authRoles.editor,
      },
      {
        path: "/employees/list",
        element: <EmployeesTable />,
        // element: <Checkoutlist />,
        auth: authRoles.employee,
      },

      {
        path: "/admin_attendance_list",
        element: <AdminList />,
        // auth: authRoles.sa,
      },
      {
        path: "/employees/add",
        element: <AddEmployee />,
        auth: authRoles.editor,
      },
      {
        path: "/employees/edit",
        element: <EditEmployee />,
        auth: authRoles.editor,
      },
      {
        path: "/checkinout/checkoutlist",
        element: <Checkinout />,
        auth: authRoles.editor,
      },
      // {
      //   path: '/employees/edit',
      //   element: <Checkoutlist />,
      //   auth: authRoles.editor
      // },
      {
        path: "/profile/profile",
        element: <Profile />,
        auth: authRoles.editor,
      },
      {
        path: "/roles/roles",
        element: <RoleList />,
        auth: authRoles.editor,
      },
      {
        path: "/roles/add",
        element: <AddRole />,
        auth: authRoles.editor,
      },
      {
        path: "/roles/Edit",
        element: <EditRole />,
        auth: authRoles.editor,
      },
      {
        path: "/approvals",
        element: <Approval />,
        auth: authRoles.editor,
      },
      {
        path: "/employee/requests",
        element: <Requests />,
        auth: authRoles.editor,
      },
      {
        path: "/setting",
        element: <SystemSettings />,
        auth: authRoles.editor,
      },
      {
        path: "/system-settings",
        element: <SystemSettings />,
        auth: authRoles.editor,
      },
      {
        path: "/employees/report",
        element: <ReportsTab />,
        auth: authRoles.editor,
      },
      {
        path: "/reports",
        element: <ReportsHub />,
        auth: authRoles.editor,
      },
      {
        path: "/employees/structure",
        element: <EmployeeSystemTab />,
        auth: authRoles.editor,
      },
      {
        path: "/employees/details/:id",
        element: <EmployeeDetailsTab />,
        auth: authRoles.editor,
      },
      {
        path: "/employees/activity-tracking",
        element: <EmployeActivityTab />,
        auth: authRoles.editor,
      },
      {
        path: "/employees/app-management",
        element: <AppManagement />,
        auth: authRoles.editor,
      },
      {
        path: "/leaves",
        element: <Leaves />,
        auth: authRoles.editor,
      },
      {
        path: "/visitor/list",
        element: <VisitorList />,
        auth: authRoles.editor,
      },
      {
        path: "/timesheet",
        element: <TimesheetReport />,
        auth: authRoles.editor,
      },
      {
        path: "/plan",
        element: <Plan />,
        auth: authRoles.editor,
      },
      {
        path: "/holiday",
        element: <HolidayCalender />,
        auth: authRoles.editor,
      },
      {
        path: "/holiday/add",
        element: <AddHoliday />,
        auth: authRoles.editor,
      },
      {
        path: "profile/change-password",
        element: <ChangePassword />,
        auth: authRoles.editor,
      },
      {
        path: "/leave/management",
        element: <LeaveType />,
      },
      {
        path: "/leave/tracker",
        element: <LeaveTracker />,
      },
      {
        path: "/leave/balances",
        element: <LeaveBalances />,
      },
      {
        path: "/leave/balance",
        element: <LeaveBalances />,
      },
      {
        path: "/employee_leave",
        element: <LeaveBalances />,
      },
      {
        path: "/leave/adjustment",
        element: <LeaveBalances />,
      },
      {
        path: "/leave/leave-report",
        element: <LeaveReport />,
      },
      {
        path: "/leave/lop-adjustment",
        element: <LeaveBalances />,
      },
      {
        path: "/add/leavetype",
        element: <LeaveSetup />,
      },
      {
        path: "/leave/setting",
        element: <LeaveSetup />,
      },
      {
        path: "/leave/setup",
        element: <LeaveSetup />,
      },
      //Leave Management system
      {
        path: "/leave/management",
        element: <LeaveType />,
      },
      {
        path: "/leave/tracker",
        element: <LeaveTracker />,
      },
      {
        path: "/leave/adjustment",
        element: <EmployeeLeaveList />,
      },
      {
        path: "/leave/leave-report",
        element: <LeaveReport />,
      },
      {
        path: "/add/leavetype",
        element: <AddLeaveType />,
      },
      {
        path: "/leave/setting",
        element: <LeaveCofiguration />,
      },
      {
        path: "/leave/setup",
        element: <LeaveSetup />,
      },
      {
        path: "/leave/reconciliation",
        element: <LeaveReconcilation />,
      },
      {
        path: "/leave/balances",
        element: <LeaveBalances />,
      },
      {
        path: "/leave/balance",
        element: <LeaveBalances />,
      },
      {
        path: "/leave/lop-adjustment",
        element: <LeaveBalances />,
      },
      {
        path: "/cron-job",
        element: <CronJobs />,
      },
      // Timesheet Management (Admin)
      {
        path: "/projects",
        element: <ProjectManagement />,
      },
      {
        path: "/timesheet-approval",
        element: <TimesheetApproval />,
      },
      {
        path: "/timesheet-report",
        element: <TimesheetReport />,
      },
    ],
  },
  // Employee module routes
  {
    element: (
      <EmployeeGuard>
        <MatxLayout />
      </EmployeeGuard>
    ),
    children: [
      {
        path: "/employee-dashboard",
        element: <EmployeeDashboard />,
        // auth: authRoles.admin,
      },
      {
        path: "/profiles/profile",
        element: <Profile />,
        auth: authRoles.editor,
      },
      {
        path: "/employees/profile",
        element: <EmployeProfileTab />,
        auth: authRoles.editor,
      },
      {
        path: "employee-profile/change-password",
        element: <ChangePassword />,
        auth: authRoles.editor,
      },
      {
        path: "/holiday-calendar",
        element: <EmployeeCalendar />,
        // auth: authRoles.admin,
      },
      {
        path: "/attendance",
        // element: <EmployeesTable />,
        element: <EmployeeAttendanceList />,
        auth: authRoles.employee,
      },
      {
        path: "/employee-leave",
        // element: <EmployeesTable />,
        element: <EmployeeLeave />,
        auth: authRoles.employee,
      },
      {
        path: "/my-activity",
        element: <EmployeeActivityLogsData />,
      },
      {
        path: "/my-timesheet",
        element: <TimesheetEntry />,
      },
    ],
  },
  // Company module routes
  {
    element: (
      <CompanyGuard>
        <MatxLayout />
      </CompanyGuard>
    ),
    children: [
      {
        path: "/admin/company/details",
        element: <AddCompany />,
        auth: authRoles.editor,
      },
      {
        path: "/system-dashboard",
        element: <SystemDashboard />,
        auth: authRoles.admin,
      },
      {
        path: "/admin/company",
        element: <CompanyList />,
        auth: authRoles.editor,
      },
    ],
  },
  {
    element: (
      <CheckInOutGaurd>
        <MatxLayout />
      </CheckInOutGaurd>
    ),
    children: [
      {
        path: "/checkInOut",
        // element: <EmployeesTable />,
        element: <Checkinout />,
        auth: authRoles.employee,
      },
    ],
  },

  {
    path: "/",
    element: (
      <RedesignShell>
        <HomePageRedesign />
      </RedesignShell>
    ),
  },
  {
    path: "/activity-tracking",
    element: (
      <LandingPage>
        <ActivityTracking></ActivityTracking>
      </LandingPage>
    ),
  },
  {
    path: "/real-time-insight",
    element: (
      <LandingPage>
        <RealTimeInsights></RealTimeInsights>
      </LandingPage>
    ),
  },
  {
    path: "/leave-management",
    element: (
      <LandingPage>
        <LeaveMangement></LeaveMangement>
      </LandingPage>
    ),
  },
  // Old misspelled slug — client-side redirect only. A server-level 301
  // for /leave-mangement should also be added wherever the nginx config
  // for timetango.in is managed (not present in this repo).
  {
    path: "/leave-mangement",
    element: <Navigate to="/leave-management" replace />,
  },
  {
    path: "/WFH-support",
    element: (
      <LandingPage>
        <WorkFromHome></WorkFromHome>
      </LandingPage>
    ),
  },
  {
    path: "/compliences-report",
    element: (
      <LandingPage>
        <DetailedComplianceReports></DetailedComplianceReports>
      </LandingPage>
    ),
  },
  {
    path: "/visitor-management",
    element: (
      <LandingPage>
        <VisitorMangement></VisitorMangement>
      </LandingPage>
    ),
  },
  {
    path: "/visitor-management",
    element: <Navigate to="/visitor-management" replace />,
  },
  {
    path: "/pricing",
    element: (
      <RedesignShell>
        <PricingRedesign />
      </RedesignShell>
    ),
  },
  {
    path: "/solutions/attendance",
    element: (<RedesignShell><AttendanceSolution /></RedesignShell>),
  },
  {
    path: "/solutions/leave",
    element: (<RedesignShell><LeaveSolution /></RedesignShell>),
  },
  {
    path: "/solutions/activity-tracking",
    element: (<RedesignShell><ActivityTrackingSolution /></RedesignShell>),
  },
  {
    path: "/solutions/timesheet",
    element: (<RedesignShell><TimesheetSolution /></RedesignShell>),
  },
  {
    path: "/solutions/visitor",
    element: (<RedesignShell><VisitorSolution /></RedesignShell>),
  },
  {
    path: "/blogs",
    element: (
      <RedesignShell>
        <Blogs></Blogs>
      </RedesignShell>
    ),
  },
  {
    path: "/faq",
    element: (
      <RedesignShell>
        <FAQ></FAQ>
      </RedesignShell>
    ),
  },
  {
    path: "/contact-us",
    element: (
      <RedesignShell>
        <ContactUs></ContactUs>
      </RedesignShell>
    ),
  },
  {
    path: "/about-us",
    element: (
      <RedesignShell>
        <AboutUs></AboutUs>
      </RedesignShell>
    ),
  },
  {
    path: "/blog-the-best-way-to-track-time-attendance-of-your-remote-team",
    element: (
      <LandingPage>
        <TheBestWayToTrackTimeAttendanceOfYourRemoteTeam></TheBestWayToTrackTimeAttendanceOfYourRemoteTeam>
      </LandingPage>
    ),
  },
  {
    path: "/blog-hr-technology-trends-to-watch-out",
    element: (
      <LandingPage>
        <HRTechnologyTrendsToWatchOut></HRTechnologyTrendsToWatchOut>
      </LandingPage>
    ),
  },
  {
    path: "/blog-automated-attendance-solutions-the-game-changer-for-Startups-and-smes",
    element: (
      <LandingPage>
        <AutomatedAttendanceSolutions></AutomatedAttendanceSolutions>
      </LandingPage>
    ),
  },
  {
    path: "/blog-optimizing-employee-schedules-with-attendance-management-software",
    element: (
      <LandingPage>
        <OptimizingEmployeeSchedulesWithAttendanceManagementSoftware></OptimizingEmployeeSchedulesWithAttendanceManagementSoftware>
      </LandingPage>
    ),
  },
  {
    path: "/blog-boost-business-efficiency-with-employee-attendance-management-software",
    element: (
      <LandingPage>
        <BoostBusinessEfficiency></BoostBusinessEfficiency>
      </LandingPage>
    ),
  },
  {
    path: "/blog-how-efficient-attendance-management-boosts-productivity-reduces-costs",
    element: (
      <LandingPage>
        <HowBoostsProductivityandReducesCosts></HowBoostsProductivityandReducesCosts>
      </LandingPage>
    ),
  },

  {
    path: "/blog-manual-vs-automated-attendance-management-why-technology-wins",
    element: (
      <LandingPage>
        <AttandanceManagement></AttandanceManagement>
      </LandingPage>
    ),
  },
  {
    path: "/blog-how-attendance-management-software-improves-accuracy-and-saves-time",
    element: (
      <LandingPage>
        <HowAttendanceManagementSoftwareImproves></HowAttendanceManagementSoftwareImproves>
      </LandingPage>
    ),
  },
  {
    path: "/blog-top-benefits-of-implementing-attendance-management-software-in-your-business",
    element: (
      <LandingPage>
        <TopBenefitsofImplementingAttendance></TopBenefitsofImplementingAttendance>
      </LandingPage>
    ),
  },
  {
    path: "/blog-future-of-employee-time-and-attendance-trends-in-management-software",
    element: (
      <LandingPage>
        <TheFutureofEmployeeTime></TheFutureofEmployeeTime>
      </LandingPage>
    ),
  },
  // {
  //   path: "/blog-why-your-company-needs-a-modern-attendance-management-system",
  //   element: (
  //     <LandingPage>
  //       <WhyYourComapnyNeeds></WhyYourComapnyNeeds>
  //     </LandingPage>
  //   ),
  // },
  {
    path: "/blog-why-your-company-needs-a-modern-attendance-management-system",
    element: (
      <LandingPage>
        <WhyYourComapnyNeeds></WhyYourComapnyNeeds>
      </LandingPage>
    ),
  },
  {
    path: "/blog-advanced-attendance-tracking-solutions-help-reduce-time-theft",
    element: (
      <LandingPage>
        <AdvancedTrackingSolutionsHelp></AdvancedTrackingSolutionsHelp>
      </LandingPage>
    ),
  },
  {
    path: "/blog-streamlining-hr-operations-the-role-of-attendance-management-software",
    element: (
      <LandingPage>
        <StreamliningHROperations></StreamliningHROperations>
      </LandingPage>
    ),
  },
  {
    path: "/blog-key-features-to-Look-for-in-an-attendance-management-system",
    element: (
      <LandingPage>
        <KeyFeaturestoLookAttandance></KeyFeaturestoLookAttandance>
      </LandingPage>
    ),
  },
  {
    path: "/blog-5-common-attendance-management-challenges-and-how-to-solve-them",
    element: (
      <LandingPage>
        <CommonAttendanceManagement></CommonAttendanceManagement>
      </LandingPage>
    ),
  },
  {
    path: "/blog-boosting-employee-accountability-with-smart-attendance-management-solutions",
    element: (
      <LandingPage>
        <BoostingEmployeeAccountability></BoostingEmployeeAccountability>
      </LandingPage>
    ),
  },
  {
    path: "/blog-enhancing-remote-work-management-with-attendance-management-app",
    element: (
      <LandingPage>
        <Enhancingremotework></Enhancingremotework>
      </LandingPage>
    ),
  },
  {
    path: "/blog-the-importance-of-attendance-management-for-Compliance-and-audits",
    element: (
      <LandingPage>
        <TheImportanceofAttendance></TheImportanceofAttendance>
      </LandingPage>
    ),
  },
  {
    path: "/blog-how-to-choose-the-right-attendance-management-software-for-your-business",
    element: (
      <LandingPage>
        <HowtochoosetherightAttendancemanagement></HowtochoosetherightAttendancemanagement>
      </LandingPage>
    ),
  },
  {
    path: "/blog-real-time-attendance-tracking-benefits-for-large-and-small-businesses",
    element: (
      <LandingPage>
        <RealTimeAttendanceTrackingBenefits></RealTimeAttendanceTrackingBenefits>
      </LandingPage>
    ),
  },
  {
    path: "/login",
    element: (
      <LogInLayout>
        <JwtLogin />
      </LogInLayout>
    ),
  },

  {
    path: "/admin/login",
    element: (
      <LogInLayout>
        <JwtAdminLogin />
      </LogInLayout>
    ),
  },

  {
    path: "/forgot-password",
    element: (
      <LogInLayout>
        <ForgotPassword />
      </LogInLayout>
    ),
  },
  {
    path: "/admin-forgot-password",
    element: (
      <LogInLayout>
        <AdminForgotPassword />
      </LogInLayout>
    ),
  },
  {
    path: "/company-register",
    element: (
      <LogInLayout>
        <JwtLRegisterCompany />
      </LogInLayout>
    ),
  },
  {
    path: "/verify-email",
    element: (
      <LogInLayout>
        <VerifyEmail />
      </LogInLayout>
    ),
  },
  {
    path: "/offer",
    element: (
      <LogInLayout>
        <OfferLanding />
      </LogInLayout>
    ),
  },

  // Client approval (public, no auth)
  {
    path: "/client-approval",
    element: <ClientApprovalView />,
  },

  // { path: '/', element: <Navigate to="dashboard/default" /> },
  { path: "*", element: <NotFound /> },
];

export default routes;
